import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Redirect } from 'react-router-dom'
import { X as XIcon, Check as CheckIcon } from 'react-feather'
import { useQueryParam } from 'use-query-params'

import LockIcon from '~/assets/img/icons/lock.svg'
import UserIcon from '~/assets/img/icons/user-icon.png'
import CameraIcon from '~/assets/img/icons/camera-icon.png'
import uploadFile from '~/api/uploadFile'
import useAxios from 'axios-hooks'
import { toast } from 'react-toastify'
import { AuthLayout } from './components/AuthLayout'
import styled from 'styled-components'
import { queries } from '~/constants/mediaQuery'

export default function InviteAccept() {
  const intl = useIntl()
  const [token] = useQueryParam('token')
  const [loading, setLoading] = useState(false)
  const [formPage, setFormPage] = useState(1)
  const [password, setPassword] = useState('')
  const [passwordShow, setPasswordShow] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false)
  const [name, setName] = useState('')
  const [avatar, setAvatar] = useState('')

  const isPasswordTouched = password.length || confirmPassword.length
  const isPasswordSame = password === confirmPassword
  const isPasswordLength = password.length >= 8
  const isPasswordUppercase = password.match(/[A-Z]/g)
  const isPasswordLowercase = password.match(/[a-z]/g)
  const isPasswordNumber = password.match(/\d/g)
  const isPasswordSpecialChar = password.match(/\W/g)
  const isRequirementsMet =
    isPasswordSame &&
    isPasswordLength &&
    isPasswordUppercase &&
    isPasswordLowercase &&
    isPasswordNumber &&
    isPasswordSpecialChar
  const isNameLength = name.trim().length >= 4

  const [, acceptInvite] = useAxios(
    {
      url: '/users/invite/accept',
      method: 'POST',
      params: {
        token,
      },
    },
    {
      manual: true,
    }
  )

  async function handleSubmit() {
    setLoading(true)
    const body = {
      password,
      name: name.trim(),
    }

    if (avatar) {
      const res = await uploadFile(avatar)
      body.avatar = res.data.uri
    }

    acceptInvite({ data: body })
      .then(() => {
        window.location.replace('/login')
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: 'This invite was already used or is expired',
          })
        )
        setLoading(false)
      })
  }

  if (!token) return <Redirect to="/login" />

  return (
    <AuthLayout
      title="Welcome to Locasty"
      description="Let's complete your account"
    >
      <StyledInviteDiv>
        {formPage === 1 ? (
          <>
            <div className="invite-form-div">
              <div className="invite-password">
                <label htmlFor="new-password">
                  <FormattedMessage id="Enter a password" />
                </label>
                <div>
                  <img src={LockIcon} alt="" />
                  <input
                    id="new-password"
                    autoComplete="new-password"
                    type={passwordShow ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••••"
                  />
                  <button
                    onClick={() => setPasswordShow(prevState => !prevState)}
                  >
                    <FormattedMessage id={passwordShow ? 'Hide' : 'Show'} />
                  </button>
                </div>
              </div>
              <div className="invite-password">
                <label htmlFor="confirm-password">
                  <FormattedMessage id="Confirm your password" />
                </label>
                <div>
                  <img src={LockIcon} alt="" />
                  <input
                    id="confirm-password"
                    autoComplete="new-password"
                    type={confirmPasswordShow ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••"
                  />
                  <button
                    className="confirm"
                    onClick={() =>
                      setConfirmPasswordShow(prevState => !prevState)
                    }
                  >
                    <FormattedMessage
                      id={confirmPasswordShow ? 'Hide' : 'Show'}
                    />
                  </button>
                </div>
              </div>
              <div className="password-requirements">
                <div
                  className={
                    isPasswordTouched
                      ? isPasswordSame
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordSame ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="Both passwords are the same" />
                </div>

                <div
                  className={
                    isPasswordTouched
                      ? isPasswordLength
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordLength ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="8 characters minimum" />
                </div>

                <div
                  className={
                    isPasswordTouched
                      ? isPasswordUppercase
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordUppercase ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="Uppercase letter" />
                </div>

                <div
                  className={
                    isPasswordTouched
                      ? isPasswordLowercase
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordLowercase ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="Lowercase letter" />
                </div>

                <div
                  className={
                    isPasswordTouched
                      ? isPasswordNumber
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordNumber ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="Number" />
                </div>

                <div
                  className={
                    isPasswordTouched
                      ? isPasswordSpecialChar
                        ? 'requirements-check'
                        : 'requirements-fail'
                      : ''
                  }
                >
                  {isPasswordTouched ? (
                    isPasswordSpecialChar ? (
                      <CheckIcon size={16} color="#48BB78" />
                    ) : (
                      <XIcon size={16} color="#E53E3E" />
                    )
                  ) : (
                    <XIcon size={16} color="#718096" />
                  )}
                  <FormattedMessage id="Special character" />
                </div>
              </div>
              <button
                className="proceed-button"
                disabled={!isPasswordTouched || !isRequirementsMet}
                onClick={() => setFormPage(2)}
              >
                <FormattedMessage id="Proceed" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="invite-form-div">
              <div className="invite-text">
                <label htmlFor="name">
                  <FormattedMessage id="Full name" />*
                </label>
                <input
                  id="name"
                  autoComplete="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={loading}
                  placeholder={intl.formatMessage({ id: 'Enter your name' })}
                />
              </div>
              <div className="invite-avatar">
                <label>
                  <FormattedMessage id="Profile picture" />
                </label>
                <div>
                  <div className="custom-avatar-input">
                    <label htmlFor="avatar" className="custom-avatar-icon">
                      <div data-disabled={loading}>
                        <img id="default-user-icon" src={UserIcon} alt="" />
                        <img hidden id="custom-user-icon" alt="" />
                      </div>
                      <div data-disabled={loading}>
                        <img src={CameraIcon} alt="" />
                      </div>
                    </label>
                    <div>
                      <FormattedMessage id="PNG or JPG allowed. Max size of 800kb." />
                    </div>
                    <div>
                      <FormattedMessage id="This photo is only visible on Locasty's dashboard." />
                    </div>
                  </div>
                  <input
                    hidden
                    id="avatar"
                    autoComplete="avatar"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    disabled={loading}
                    onChange={e => {
                      const file = e.target.files[0]
                      const reader = new FileReader()
                      const img = document.getElementById('default-user-icon')
                      const customImg = document.getElementById(
                        'custom-user-icon'
                      )

                      if (!file.type.match(/image\/(png|jpg|jpeg)/g)) return
                      setAvatar(file)

                      if (!file) {
                        img.hidden = false
                        customImg.hidden = true
                        customImg.src = ''
                      } else {
                        reader.onload = function (e) {
                          img.hidden = true
                          customImg.hidden = false
                          customImg.src = e.target.result
                        }

                        reader.readAsDataURL(file)
                      }
                    }}
                  />
                </div>
              </div>
              <button
                className="proceed-button"
                disabled={!isNameLength || loading}
                onClick={handleSubmit}
              >
                <FormattedMessage id="Finish" />
              </button>
            </div>
          </>
        )}
      </StyledInviteDiv>
    </AuthLayout>
  )
}

const StyledInviteDiv = styled.div`
  width: 100%;

  .invite-form-div {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;

    .proceed-button {
      display: flex;
      min-width: 30.5625rem;
      max-width: 30.5625rem;
      justify-content: center;
      align-items: center;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 0.25rem;
      background-color: #7367f0;
      color: #fff;
      font-size: 1.125rem;
      font-weight: 700;

      @media (${queries.less.mds}) {
        min-width: 100%;
      }

      &:disabled {
        background-color: #b9b3f7;
        cursor: not-allowed;
      }
    }
  }

  .password-requirements {
    width: 100%;
    max-width: 30.5625rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: #718096;
    font-size: 0.75rem;
    font-weight: 700;

    @media (${queries.less.mds}) {
      max-width: 100%;
    }

    > div {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &.requirements-check {
        color: #48bb78;
      }

      &.requirements-fail {
        color: #e53e3e;
      }
    }
  }

  .invite-password {
    position: relative;
    display: flex;
    min-width: 30.5625rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    @media (${queries.less.mds}) {
      min-width: 100%;
    }

    label {
      font-weight: 700;
      color: #000;
      font-size: 1rem;
    }

    > div {
      width: 100%;
      max-width: 30.5625rem;
      height: 3rem;

      input {
        height: 100%;
        width: 75%;
        border: 1px solid #d8d8d8;
        border-radius: 0.25rem 0 0 0.25rem;
        padding: 0.5rem 1rem 0.5rem 3rem;

        &::placeholder {
          color: #cfcfcf;
        }
      }

      button {
        height: 100%;
        width: 25%;
        border: 1px solid #d8d8d8;
        border-left: none;
        border-radius: 0 0.25rem 0.25rem 0;
        background-color: #fafafa;
        color: #6d7278;
        font-weight: 700;
        font-size: 1rem;
        padding: 0.5rem 1rem;

        &.confirm {
          position: relative;
          top: -1px;
        }
      }

      img {
        position: absolute;
        left: 1rem;
        bottom: -2px;
        transform: translateY(-50%);
      }
    }
  }

  .invite-text {
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    label {
      font-weight: 700;
      color: #000;
      font-size: 1rem;
    }

    input {
      height: 3rem;
      width: 100%;
      max-width: 30.5625rem;
      border: 1px solid #d8d8d8;
      border-radius: 0.25rem 0 0 0.25rem;
      padding: 0.5rem 1rem 0.5rem 1rem;

      &::placeholder {
        color: #cfcfcf;
      }
    }
  }

  .invite-avatar {
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;

    label {
      font-weight: 700;
      color: #000;
      font-size: 1rem;
    }

    .custom-avatar-input {
      display: flex;
      max-width: 30.5625rem;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: #a8a8a8;
      font-weight: 700;
      font-size: 0.8rem;

      .custom-avatar-icon {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 1rem;

        #custom-user-icon {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 99999999px;
          aspect-ratio: 1/1;
        }

        > div:nth-child(1) {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 9.5rem;
          height: 9.5rem;
          aspect-ratio: 1/1;
          border: 1px solid #d1d1d1;
          border-radius: 99999999px;
          background-color: #fff;
          box-shadow: -1px 2px 6px 0px rgba(0, 0, 0, 0.16);
          cursor: pointer;

          &[data-disabled='true'] {
            cursor: not-allowed;
          }
        }

        > div:nth-child(2) {
          position: absolute;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 2.5rem;
          height: 2.5rem;
          aspect-ratio: 1/1;
          border-radius: 99999999px;
          background-color: #0d86ea;
          padding-left: 1px;
          box-shadow: -1px 2px 6px 0px rgba(0, 0, 0, 0.16);
          cursor: pointer;

          &[data-disabled='true'] {
            cursor: not-allowed;
          }
        }
      }
    }
  }
`
